import React from 'react';
import { Link } from 'react-router-dom';
import type { PassportFeatureFlags } from '../../services/api/passport';

export type PassportNavId = 'cars' | 'overview' | 'recap' | 'collections' | 'campaigns' | 'settings';

type Props = {
  active: PassportNavId;
  flags?: PassportFeatureFlags | null;
  year?: number;
};

/**
 * Shared Passport destinations — keeps hub and subsections one product.
 * Horizontal scroll on narrow viewports; aria-current on active page.
 */
export const PassportSubnav: React.FC<Props> = ({
  active,
  flags,
  year = new Date().getFullYear(),
}) => {
  const items: Array<{ id: PassportNavId; to: string; label: string; show: boolean }> = [
    { id: 'cars', to: '/passport', label: 'Passport', show: true },
    { id: 'overview', to: '/passport/activity', label: 'Activity', show: true },
    { id: 'recap', to: `/passport/recap?year=${year}`, label: 'Recap', show: true },
    {
      id: 'collections',
      to: '/passport/collections',
      label: 'Sets',
      show: flags?.passport_collections_enabled !== false,
    },
    {
      id: 'campaigns',
      to: '/passport/campaigns',
      label: 'Hunts',
      show: flags?.sponsor_campaigns_enabled !== false,
    },
    { id: 'settings', to: '/passport/settings', label: 'Privacy', show: true },
  ];

  return (
    <nav
      className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 scrollbar-thin"
      aria-label="Passport sections"
    >
      {items
        .filter((item) => item.show)
        .map((item) => {
          const selected = item.id === active;
          return (
            <Link
              key={item.id}
              to={item.to}
              aria-current={selected ? 'page' : undefined}
              className={`shrink-0 min-h-[40px] inline-flex items-center rounded-full border px-3.5 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/60 ${
                selected
                  ? 'border-white/20 bg-white text-black'
                  : 'border-white/[0.1] bg-transparent text-zinc-300 hover:border-white/20 hover:text-white'
              }`}
            >
              {item.label}
            </Link>
          );
        })}
    </nav>
  );
};

export default PassportSubnav;
